import type { DavsTaxonomyAttribute } from '@finn-no/mobility-item-insertion-shared';
import { i18n } from '@lingui/core';
import { useEffect, useRef, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import type { ModelAndMakeWidgetModel, ValueOption } from '../../../types/widget.js';
import { Combobox } from '../combobox.js';

interface ModelAndMakeWidgetProps extends Omit<ModelAndMakeWidgetModel, 'type' | 'mandatory'> {
  attribute: DavsTaxonomyAttribute;
  helpText?: string;
}

const lookupMake = (
  values: DavsTaxonomyAttribute['values'],
  make_and_modelId: number | undefined,
): { id: number; label: string } | undefined => {
  const modelValue = values.find((value) => value.id === make_and_modelId);

  // TODO: Why are we returning a model id as a make id?
  if (modelValue && !modelValue.parentId) {
    return { id: modelValue.id, label: modelValue.text }; // Return the full model as 'make'
  }

  const makeValue = values.find((value) => value.id === modelValue?.parentId);
  if (makeValue?.id && makeValue?.text) {
    return { id: makeValue.id, label: makeValue.text }; // Return both 'id' and 'label'
  }

  return undefined;
};

export const ControlledModelAndMakeWidget = ({ name, attribute, helpText, className }: ModelAndMakeWidgetProps) => {
  const [localErrorField, setLocalErrorField] = useState<'Make' | 'Model' | undefined>('Make');
  const form = useFormContext();
  const isMounted = useRef(false);

  const makeValidationError = i18n.t({
    id: 'mobility-item-insertion.car.make.validationError',
    message: 'Please select a make',
    comment: 'Validation error when make is not selected',
  });

  const modelValidationError = i18n.t({
    id: 'mobility-item-insertion.car.model.validationError',
    message: 'Please select a model',
    comment: 'Validation error when model is not selected',
  });

  const make_and_model = useController({
    name: 'model_and_make',
    rules: {
      validate: () => {
        if (!makeId) {
          setLocalErrorField('Make');
          return makeValidationError;
        }
        if (!modelId) {
          setLocalErrorField('Model');
          return modelValidationError;
        }

        return true;
      },
    },
  });

  const initialMake = make_and_model.field.value ? lookupMake(attribute.values, make_and_model.field.value) : undefined;
  const [makeId, setMakeId] = useState<number | undefined>(initialMake?.id);
  const [modelId, setModelId] = useState<number | undefined>(make_and_model.field.value);

  useEffect(() => {
    if (isMounted.current) {
      form.trigger('model_and_make');
    } else {
      isMounted.current = true;
    }
  }, [makeId, modelId, form.trigger]);

  const makeOptions =
    attribute?.values
      .filter((make) => !('parentId' in make))
      .map((make) => ({ id: make.id, code: `${make.id}`, label: make.text })) ?? [];

  const modelOptions = makeId
    ? (attribute?.values
        .filter((model) => ('parentId' in model ? model.parentId === makeId : false) && model.persistable)
        .map((model) => ({
          id: model.id,
          code: `${model.id}`,
          label: model.text,
        })) ?? [])
    : [];

  const invalidMake = !!make_and_model.fieldState.error && localErrorField === 'Make';
  const invalidModel = !!make_and_model.fieldState.error && localErrorField === 'Model';

  const makeHelpText = invalidMake ? make_and_model.fieldState.error?.message : helpText;
  const modelHelpText = invalidModel ? make_and_model.fieldState.error?.message : helpText;

  const makeLabel = i18n.t({
    id: 'mobility-item-insertion.car.make.label',
    message: 'Car make',
    comment: 'Label for the dropdown where users can select the car make',
  });
  const modelLabel = i18n.t({
    id: 'mobility-item-insertion.car.model.label',
    message: 'Car model',
    comment: 'Label for the dropdown where users can select the car model',
  });

  const handleMakeSelect = (option?: ValueOption) => {
    const makeId = option?.id;
    setMakeId(Number(makeId));
    setModelId(undefined);

    if (!makeId) {
      form.setValue('model_and_make', undefined, { shouldValidate: false });
      form.setError('model_and_make', {
        type: 'required',
        message: makeValidationError,
      });
      setLocalErrorField('Make');
      return;
    }

    const hasModels = makeId && attribute.values.some((model) => model.parentId === makeId);

    if (!hasModels) {
      form.setValue('model_and_make', makeId, { shouldValidate: false });
      form.clearErrors('model_and_make');
      return;
    }

    form.setValue('model_and_make', undefined, { shouldValidate: false });
    if (make_and_model.fieldState.error && localErrorField === 'Make') {
      form.clearErrors('model_and_make');
    }
    form.setError('model_and_make', {
      type: 'required',
      message: modelValidationError,
    });
    setLocalErrorField('Model');
  };

  const handleModelSelect = (option?: ValueOption) => {
    const modelId = option?.id;
    setModelId(Number(modelId));

    if (modelId) {
      make_and_model.field.onChange(modelId);
      form.clearErrors('model_and_make');

      form.setValue('model_and_make', modelId, { shouldValidate: true });
      setLocalErrorField(undefined);
    } else {
      make_and_model.field.onChange('');
      form.setError('model_and_make', {
        type: 'required',
        message: modelValidationError,
      });
      setLocalErrorField('Model');
    }
  };

  // Auto-select exact match or single match for make on blur
  const handleMakeBlur = (inputValue: string) => {
    make_and_model.field.onBlur();

    if (!inputValue.trim()) return;

    const currentMake = makeOptions.find((option) => option.id === makeId);
    if (currentMake && currentMake.label.toLowerCase() === inputValue.toLowerCase()) return;

    const exactMatch = makeOptions.find((option) => option.label.toLowerCase() === inputValue.toLowerCase());

    if (exactMatch) {
      handleMakeSelect(exactMatch);
      return;
    }

    if (inputValue.trim().length > 0) {
      const partialMatches = makeOptions.filter((option) =>
        option.label.toLowerCase().includes(inputValue.toLowerCase()),
      );
      if (partialMatches.length === 1) {
        handleMakeSelect(partialMatches[0]);
      }
    }
  };

  // Auto-select exact match or single match for model on blur
  const handleModelBlur = (inputValue: string) => {
    make_and_model.field.onBlur();

    if (!inputValue.trim()) return;

    const currentModel = modelOptions.find((option) => option.id === modelId);
    if (currentModel && currentModel.label.toLowerCase() === inputValue.toLowerCase()) return;

    const exactMatch = modelOptions.find((option) => option.label.toLowerCase() === inputValue.toLowerCase());

    if (exactMatch) {
      handleModelSelect(exactMatch);
      return;
    }

    if (inputValue.trim().length > 0) {
      const partialMatches = modelOptions.filter((option) =>
        option.label.toLowerCase().includes(inputValue.toLowerCase()),
      );
      if (partialMatches.length === 1) {
        handleModelSelect(partialMatches[0]);
      }
    }
  };

  return (
    <div ref={make_and_model.field.ref} data-attribute={name}>
      <Combobox
        name="make"
        label={makeLabel}
        options={makeOptions}
        onSelect={handleMakeSelect}
        invalid={invalidMake}
        helpText={makeHelpText}
        value={makeId}
        className={className}
        data-testid="make"
        onBlur={handleMakeBlur}
      />
      {makeId && modelOptions.length > 0 ? (
        <Combobox
          name="model_and_make"
          label={modelLabel}
          options={modelOptions}
          onSelect={handleModelSelect}
          invalid={invalidModel}
          helpText={modelHelpText}
          value={modelId}
          className={className}
          data-testid="model"
          onBlur={handleModelBlur}
        />
      ) : null}
    </div>
  );
};
